import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Grid, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Select } from "@/components/elements/Select";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TCompanyExist, TJobForm } from "@/features/job/types/JobForm";
import { useEnum } from "@/api/services/global/enum";
import { useGetBusinessClassificationList, useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { TOption } from "@/types/common";
import { black, blue } from "@/theme/colors";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Combobox } from "@/components/elements/Combobox";
import { DuplicationCompanyScan } from "@/features/job/components/job-scan-image/DuplicationCompanyScan";

type TJobBasicInfoProps = {
  isDraft?: boolean;
  isLoadingCompany: boolean;
  companyOptions: TOption[];
  openCreateCompanyModal: () => void;
  isLoadingRecruitmentSource: boolean;
  recruitmentSourceOptions: TOption[];
  openCreateRecruitmentSourceModal: () => void;
  companySpecificIndustryIdWatchField?: number | null;
  companyExists?: TCompanyExist[];
  onCompanyChange?: (companyName: string) => void;
};

export const JobBasicInfo: FC<TJobBasicInfoProps> = ({
  isDraft,
  isLoadingCompany,
  companyOptions,
  openCreateCompanyModal,
  isLoadingRecruitmentSource,
  recruitmentSourceOptions,
  openCreateRecruitmentSourceModal,
  companySpecificIndustryIdWatchField,
  companyExists,
  onCompanyChange,
}) => {
  const methods = useFormContext<TJobForm>();
  const { control, setValue } = methods;
  const { data: clientTypeOptions } = useEnum("EClientType");
  const {
    companySpecificIndustryOptions,
    isLoading: isLoadingCompanySpecificIndustry,
    getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry,
  } = useGetCompanySpecificIndustryList();
  const { businessClassificationOptions, isLoading: isLoadingBusinessClassification } = useGetBusinessClassificationList();

  const companyIdWatchField = useWatch({
    control,
    name: "companyId",
  });

  const companySpecificBusinessClassificationIdsWatchField = useWatch({
    control,
    name: "companySpecificBusinessClassificationIds",
  });

  const recruitmentSourceIdsWatchField = useWatch({
    control,
    name: "recruitmentSourceIds",
  });

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="基本情報" />} showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormField control={control} name="companyId">
              <Combobox
                options={companyOptions}
                loading={isLoadingCompany}
                onChange={(value) => {
                  const selectedCompany = companyOptions.find((opt) => opt.value === value);
                  if (selectedCompany && onCompanyChange) {
                    onCompanyChange(selectedCompany.label);
                  } else if (onCompanyChange) {
                    onCompanyChange("");
                  }
                }}
              />
            </FormField>
            {companyExists && companyExists.length > 0 && <DuplicationCompanyScan listCompanyExists={companyExists} maxWidth={417} />}
            <Stack direction="row" alignItems="center" mt={1}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  cursor: companyIdWatchField ? "not-allowed" : "pointer",
                  opacity: companyIdWatchField ? 0.5 : 1,
                  pointerEvents: companyIdWatchField ? "none" : "auto",
                }}
                onClick={openCreateCompanyModal}
              >
                <AddCircleOutlineIcon
                  sx={{
                    color: companyIdWatchField ? black[60] : blue[80],
                    fontSize: 18,
                  }}
                />
                <Typography variant="cap12">新規企業登録</Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="managementNumber">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="jobName">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="companySpecificIndustryId" required={!isDraft}>
              <Select
                onChange={() => {
                  setValue("companySpecificBusinessClassificationIds", [], {
                    shouldValidate: true,
                  });
                  setValue("specificSkillCertificateIds", [], {
                    shouldValidate: true,
                  });
                }}
                placeholder="選択してください"
                loading={isLoadingCompanySpecificIndustry}
                options={companySpecificIndustryOptions}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="companySpecificBusinessClassificationIds">
              <SelectMulti
                value={companySpecificBusinessClassificationIdsWatchField ? companySpecificBusinessClassificationIdsWatchField.map(String) : []}
                options={
                  companySpecificIndustryIdWatchField
                    ? getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(companySpecificIndustryIdWatchField)
                    : []
                }
                placeholder="選択してください"
                disabled={!companySpecificIndustryIdWatchField}
                separator="、"
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="businessClassificationId" required={!isDraft}>
              <Select placeholder="選択してください" options={businessClassificationOptions} loading={isLoadingBusinessClassification} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="recruitmentNumber" required={!isDraft}>
              <Input placeholder="入力してください" endAdornment="人" clearable={false} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="referralFee">
              <Input placeholder="入力してください" endAdornment="円" clearable={false} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="referralFeeCost">
              <Input placeholder="入力してください" endAdornment="円" clearable={false} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="recruitmentSourceIds">
              <SelectMulti
                loading={isLoadingRecruitmentSource}
                value={recruitmentSourceIdsWatchField ? recruitmentSourceIdsWatchField.map(String) : []}
                options={recruitmentSourceOptions}
                placeholder="選択してください"
                separator="、"
              />
            </FormField>
            <Stack direction="row" alignItems="center" mt={1}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={openCreateRecruitmentSourceModal}>
                <AddCircleOutlineIcon
                  sx={{
                    color: blue[80],
                    fontSize: 18,
                  }}
                />
                <Typography variant="cap12">新規求人材獲得ルート登録</Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="clientType">
              <SelectMulti placeholder="選択してください" options={clientTypeOptions} separator="、" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="recruiterNote">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </InnerBox>
    </Stack>
  );
};
